import './styles.scss';

import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@deliveryhero/pd-cookbook/components/Box';
import Button from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Typography } from '@deliveryhero/pd-cookbook/components/Typography';

import { AppRoute } from 'share/types/routes';
import useInterval from 'src/hooks/useInterval/useInterval';

import { initialState } from './reducer';

const TWO_FA_CODE_LENGTH = 4;
const PATTERN = '[0-9]{1}';

interface TwoFactorInputProps {
  resendInterval?: number;
  expiryInterval?: number;
  state: typeof initialState;
  isSubmitting: boolean;
  onSubmit: (otpCode?: string) => Promise<void>;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function TwoFactorInput({
  resendInterval = 30,
  expiryInterval = 300,
  state,
  isSubmitting,
  onSubmit,
  setIsSubmitting
}: TwoFactorInputProps) {
  const navigate = useNavigate();
  const inputsRef = useRef<Array<HTMLInputElement>>([]);
  const [submitBool, setSubmitBool] = useState(false);

  const handleResendCode = () => {
    onSubmit();
  };
  useEffect(() => {
    inputsRef.current[0].focus();
  }, []);

  const sendResult = () => {
    setIsSubmitting(true);
    const res = inputsRef.current.map((input) => input.value).join('');
    if (res.length === TWO_FA_CODE_LENGTH) {
      onSubmit(res);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value, nextElementSibling }
    } = event;
    if (value.length > 1) {
      event.target.value = value.charAt(0);
      if (nextElementSibling !== null) {
        (nextElementSibling as HTMLInputElement).focus();
      }
    } else {
      if (value.match(PATTERN)) {
        if (nextElementSibling !== null) {
          (nextElementSibling as HTMLInputElement).focus();
        }
      } else {
        event.target.value = '';
      }
    }
    setSubmitBool(inputsRef.current.every((input) => input.value.length === 1));
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const target = e.target as HTMLInputElement;
    const previousEle = target.previousElementSibling as HTMLInputElement;
    const nextEle = target.nextElementSibling as HTMLInputElement;

    switch (key) {
      case 'Backspace':
        if (target.value === '') {
          if (previousEle !== null) {
            e.preventDefault();
            previousEle.value = '';
            previousEle.focus();
          }
        } else {
          target.value = '';
        }
        break;
      case 'ArrowLeft':
        if (previousEle !== null) {
          e.preventDefault();
          previousEle.focus();
        }
        break;
      case 'ArrowRight':
        if (nextEle) {
          e.preventDefault();
          nextEle.focus();
        }
      default:
        break;
    }
    setSubmitBool(inputsRef.current.every((input) => input.value.length === 1));
  };

  const { seconds: resendSeconds, reset: resetResend } = useInterval(
    true,
    resendInterval
  );
  const { seconds: expirySeconds, reset: resetExpiry } = useInterval(
    true,
    expiryInterval
  );
  const formattedExpiry = `${Math.floor(expirySeconds / 60)}:${(
    expirySeconds % 60
  )
    .toString()
    .padStart(2, '0')} minutes`;
  const handleClearInputValues = () => {
    if (inputsRef.current) {
      for (let i = 0; i < inputsRef.current.length; i++) {
        inputsRef.current[i].value = '';
      }
      inputsRef.current[0].focus();
    }
    setSubmitBool(inputsRef.current.every((input) => input.value.length === 1));
  };

  const onResendCode = () => {
    resetResend();
    if (expirySeconds <= 0) {
      resetExpiry();
    }
    handleResendCode();
    handleClearInputValues();
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedValue = e.clipboardData.getData('Text');

    let currentInput = 0;

    for (let i = 0; i < pastedValue.length; i++) {
      const pastedCharacter = pastedValue.charAt(i);
      const currentValue = inputsRef.current[currentInput].value;
      if (pastedCharacter.match(PATTERN)) {
        if (!currentValue) {
          inputsRef.current[currentInput].value = pastedCharacter;
          if (inputsRef.current[currentInput].nextElementSibling !== null) {
            (
              inputsRef.current[currentInput]
                .nextElementSibling as HTMLInputElement
            ).focus();
            currentInput++;
          }
        }
      }
    }
    setSubmitBool(inputsRef.current.every((input) => input.value.length === 1));
  };

  return (
    <Box
      data-testid="two-factor-authentication-send-container"
      className="two-factor-authentication-container"
    >
      <Box
        className="two-factor-authentication-code"
        data-testid="two-factor-authentication-code"
      >
        <div>
          {[...new Array(TWO_FA_CODE_LENGTH)].map((_, i) => {
            return (
              <input
                key={i}
                autoFocus={i === 0}
                ref={(el: HTMLInputElement) => {
                  inputsRef.current[i] = el;
                }}
                maxLength={1}
                type="tel"
                inputMode="numeric"
                onKeyDown={handleOnKeyDown}
                onFocus={handleOnFocus}
                onPaste={handleOnPaste}
                pattern={PATTERN}
                min="0"
                max="9"
                autoCorrect="off"
                autoCapitalize="off"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                spellCheck={false}
                onChange={handleInputChange}
                data-testid="two-factor-authentication-code-item"
                className={`two-factor-authentication-code-item ${
                  state.hasVerificationError ? 'wrong' : ''
                }`}
              />
            );
          })}
        </div>
        {state.hasVerificationError ? (
          <Typography type="label-sm" marginTop="xs" color="error">
            Incorrect code. Try again.
          </Typography>
        ) : null}
      </Box>
      {expirySeconds > 0 ? (
        <Typography
          type="paragraph-sm"
          marginBottom="md"
          className="two-factor-authentication-expiry"
        >
          OTP will expire in&nbsp;
          <Typography type="label-md">{formattedExpiry}</Typography>
        </Typography>
      ) : null}

      <div className="two-factor-authentication-button">
        <Button
          variant="primary"
          onClick={sendResult}
          disabled={!submitBool}
          status={isSubmitting ? 'is-loading' : 'is-idle'}
          data-testid={`two-factor-authentication-submit-button-${
            !submitBool ? 'disable' : 'active'
          }`}
          className="item-first"
        >
          Submit
        </Button>
        <Button
          variant="secondary"
          status={state.isRequesting ? 'is-loading' : 'is-idle'}
          onClick={onResendCode}
          disabled={resendSeconds > 0 || state.isRequesting}
          data-testid={`two-factor-authentication-resend-button-${
            resendSeconds > 0 ? 'disable' : 'active'
          }`}
          className="item-last"
        >
          {resendSeconds > 0
            ? 'Resend OTP in %s seconds'.replace('%s', String(resendSeconds))
            : 'Resend OTP'}
        </Button>
      </div>
      <div className="two-factor-authentication-login-button">
        <Button
          variant="text"
          onClick={() => navigate(AppRoute.Login, { replace: true })}
          layout="full-width-primary"
        >
          Back to login
        </Button>
      </div>
    </Box>
  );
}
